import { useEffect, useState } from 'react';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';

interface BranchState {
  branchOf: { mainId: string; mainName: string; name: string; mergedAt?: number } | null;
  branches: { id: string; name: string; createdAt: number; mergedAt?: number }[];
}
interface Preview {
  changes: { nodeId: string; name: string; kind: 'added' | 'removed' | 'changed'; props?: string[] }[];
  conflicts: { nodeId: string; name: string; prop: string; main: unknown; branch: unknown }[];
}

export async function createBranch(name: string) {
  const r = await fetch(`/api/files/${app.fileId}/branches`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) });
  const j = await r.json();
  if (!r.ok) return app.toast(j.error ?? 'Could not create a branch');
  app.connect(j.id);
  app.toast(`You're on branch “${name}”. Merge it back when you're done.`);
}

/** On a branch: a banner with "Review & merge". */
export function BranchBanner() {
  const fileId = useApp('doc', () => app.fileId);
  const [state, setState] = useState<BranchState | null>(null);
  const [review, setReview] = useState<Preview | null>(null);
  const [policy, setPolicy] = useState<'branch' | 'main'>('branch');
  useEffect(() => {
    setState(null);
    setReview(null);
    if (!fileId) return;
    fetch(`/api/files/${fileId}/branches`)
      .then((r) => r.json())
      .then(setState)
      .catch(() => {});
  }, [fileId]);
  const b = state?.branchOf;
  if (!b) return null;
  const open = async () => setReview(await (await fetch(`/api/files/${fileId}/merge?policy=${policy}`)).json());
  const merge = async () => {
    const r = await fetch(`/api/files/${fileId}/merge`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ policy }) });
    const j = await r.json();
    if (!r.ok) return app.toast(j.error ?? 'Merge failed');
    setReview(null);
    app.toast(`Merged ${j.merged} change${j.merged === 1 ? '' : 's'} into ${b.mainName}`);
    app.connect(j.mainId);
  };
  return (
    <>
      <div className="branch-banner" onPointerDown={(e) => e.stopPropagation()}>
        <span>
          ⑂ Branch <b>{b.name}</b> of {b.mainName}
        </span>
        <button className="btn secondary" onClick={() => app.connect(b.mainId)}>
          Open main
        </button>
        <button className="btn primary" onClick={open}>
          Review &amp; merge
        </button>
      </div>
      {review && (
        <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && setReview(null)}>
          <div className="modal" style={{ width: 'min(520px, calc(100vw - 32px))' }}>
            <header>
              <span>
                Merge “{b.name}” into {b.mainName}
              </span>
              <button className="icon-btn" onClick={() => setReview(null)}>
                ✕
              </button>
            </header>
            <div className="body">
              {review.changes.length === 0 && <div style={{ color: 'var(--text-2)' }}>No changes since the branch started.</div>}
              {review.changes.map((c) => (
                <div key={c.nodeId + c.kind} className="merge-row">
                  <span className={`merge-kind ${c.kind}`}>{c.kind}</span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ color: 'var(--text-2)' }}>{c.props?.join(', ')}</span>
                </div>
              ))}
              {review.conflicts.length > 0 && (
                <>
                  <h5>
                    {review.conflicts.length} conflict{review.conflicts.length === 1 ? '' : 's'}: changed on both sides
                  </h5>
                  {review.conflicts.map((c, i) => (
                    <div key={i} className="merge-row">
                      <span style={{ flex: 1 }}>
                        {c.name} · {c.prop}
                      </span>
                      <span style={{ color: 'var(--text-2)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        main {JSON.stringify(c.main)?.slice(0, 40)} · branch {JSON.stringify(c.branch)?.slice(0, 40)}
                      </span>
                    </div>
                  ))}
                  <div className="row" style={{ gap: 6, marginTop: 6 }}>
                    <span style={{ color: 'var(--text-2)' }}>On conflicts keep</span>
                    <select className="share-select" value={policy} onChange={(e) => setPolicy(e.target.value as 'branch' | 'main')}>
                      <option value="branch">the branch's version</option>
                      <option value="main">main's version</option>
                    </select>
                  </div>
                </>
              )}
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                <span style={{ color: 'var(--text-2)', flex: 1 }}>A version of main is saved first.</span>
                <button className="btn primary" disabled={!review.changes.length} onClick={merge}>
                  Merge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
